import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Plus, Edit, Trash2, X, Save, Music, Upload, Check, Volume2, Moon, Waves, CloudRain, Trees, Wind, Bird, Flame, Play, Pause } from 'lucide-react';

// Available icons for ambient sounds
const AVAILABLE_ICONS = [
  { key: 'Moon', icon: Moon, label: 'Moon' },
  { key: 'Waves', icon: Waves, label: 'Waves' },
  { key: 'CloudRain', icon: CloudRain, label: 'Rain' },
  { key: 'Trees', icon: Trees, label: 'Forest' },
  { key: 'Wind', icon: Wind, label: 'Wind' },
  { key: 'Bird', icon: Bird, label: 'Birds' },
  { key: 'Flame', icon: Flame, label: 'Fire' },
  { key: 'Music', icon: Music, label: 'Music' },
  { key: 'Volume2', icon: Volume2, label: 'Sound' },
];

const ICON_MAP = {
  Moon, Waves, CloudRain, Trees, Wind, Bird, Flame, Music, Volume2
};

export default function ManageAmbientSounds() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [sounds, setSounds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [playingSound, setPlayingSound] = useState(null);
  const audioInputRef = useRef(null);
  const audioPreviewRef = useRef(null);
  
  const [formData, setFormData] = useState({
    sound_key: '',
    name: '',
    description: '',
    audio_url: '',
    icon: 'Music',
    is_active: true
  });

  useEffect(() => {
    loadSounds();
  }, []);

  const loadSounds = async () => {
    try {
      const response = await axios.get(`${API}/ambient-sounds/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSounds(response.data);
    } catch (error) {
      console.error('Failed to load ambient sounds:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingKey) {
        await axios.put(
          `${API}/ambient-sounds/${editingKey}`,
          {
            name: formData.name,
            description: formData.description,
            audio_url: formData.audio_url,
            icon: formData.icon,
            is_active: formData.is_active
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/ambient-sounds`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      resetForm();
      loadSounds();
    } catch (error) {
      console.error('Failed to save ambient sound:', error);
      alert('Failed to save ambient sound: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (sound) => {
    setFormData({
      sound_key: sound.sound_key,
      name: sound.name,
      description: sound.description || '',
      audio_url: sound.audio_url,
      icon: sound.icon,
      is_active: sound.is_active
    });
    setEditingKey(sound.sound_key);
    setShowForm(true);
  };

  const handleDelete = async (soundKey) => {
    if (!window.confirm('Are you sure you want to delete this ambient sound?')) return;
    
    try {
      await axios.delete(`${API}/ambient-sounds/${soundKey}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadSounds();
    } catch (error) {
      console.error('Failed to delete ambient sound:', error);
      alert('Failed to delete ambient sound');
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress('Uploading audio...');
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/audio`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, audio_url: response.data.url }));
      setUploadProgress(`Audio uploaded successfully!`);
    } catch (error) {
      console.error('Failed to upload audio:', error);
      setUploadProgress('Failed to upload audio: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(''), 5000);
    }
  };

  const togglePlayPreview = (sound) => {
    if (playingSound === sound.sound_key) {
      audioPreviewRef.current?.pause();
      setPlayingSound(null);
    } else {
      const audioUrl = sound.audio_url.startsWith('http') 
        ? sound.audio_url 
        : `${API.replace('/api', '')}${sound.audio_url}`;
      
      if (audioPreviewRef.current) {
        audioPreviewRef.current.src = audioUrl;
        audioPreviewRef.current.play();
        setPlayingSound(sound.sound_key);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      sound_key: '',
      name: '',
      description: '',
      audio_url: '',
      icon: 'Music',
      is_active: true
    });
    setEditingKey(null);
    setShowForm(false);
    setUploadProgress('');
  };

  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || Music;
  };

  // Suggested sound keys
  const suggestedKeys = ['ocean', 'rain', 'forest', 'wind', 'birds', 'fire', 'waterfall', 'thunderstorm'];
  const usedKeys = sounds.map(s => s.sound_key);
  const availableKeys = suggestedKeys.filter(k => !usedKeys.includes(k));

  return (
    <div className="space-y-6" data-testid="manage-ambient-sounds">
      {/* Hidden audio element for preview */}
      <audio 
        ref={audioPreviewRef} 
        onEnded={() => setPlayingSound(null)}
        preload="auto"
      />

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Music className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Ambient Sounds
          </h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="cosmic-btn flex items-center gap-2"
          data-testid="add-sound-btn"
        >
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span>{showForm ? 'Cancel' : 'Add Sound'}</span>
        </button>
      </div>

      <p className="text-sm" style={{ color: currentColors.textMuted }}>
        Upload ambient sounds (ocean, rain, forest, etc.) for the meditation timer. Users can select these sounds to play during their meditation sessions.
      </p>

      {showForm && (
        <div className="glass-card p-6">
          <h3 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>
            {editingKey ? 'Edit Ambient Sound' : 'Add New Ambient Sound'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Sound Key *
                  <span className="text-xs ml-2">(unique identifier, lowercase)</span>
                </label>
                <input
                  type="text"
                  value={formData.sound_key}
                  onChange={(e) => setFormData({ ...formData, sound_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  className="cosmic-input w-full"
                  placeholder="e.g., ocean, rain, forest"
                  required
                  disabled={!!editingKey}
                  data-testid="sound-key-input"
                />
                {!editingKey && availableKeys.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {availableKeys.map(key => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, sound_key: key, name: key.charAt(0).toUpperCase() + key.slice(1) })}
                        className="text-xs px-2 py-1 rounded transition-all hover:opacity-80"
                        style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Display Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="cosmic-input w-full"
                  placeholder="e.g., Ocean Waves"
                  required
                  data-testid="sound-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Description (optional)</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="cosmic-input w-full"
                placeholder="e.g., Soothing ocean waves for deep relaxation"
              />
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-sm mb-2" style={{ color: currentColors.textMuted }}>Icon *</label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_ICONS.map(({ key, icon: IconComponent, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: key })}
                    className={`p-3 rounded-lg transition-all flex flex-col items-center gap-1 ${
                      formData.icon === key ? 'ring-2' : ''
                    }`}
                    style={{ 
                      backgroundColor: formData.icon === key ? `${currentColors.primary}20` : `${currentColors.textMuted}10`,
                      ringColor: currentColors.primary,
                      color: formData.icon === key ? currentColors.primary : currentColors.textMuted
                    }}
                  >
                    <IconComponent className="w-6 h-6" />
                    <span className="text-xs">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Audio Upload */}
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: `${currentColors.secondary}10`, border: `1px solid ${currentColors.secondary}30` }}
            >
              <label className="block text-sm mb-2 flex items-center gap-2" style={{ color: currentColors.text }}>
                <Volume2 className="w-4 h-4" style={{ color: currentColors.secondary }} />
                Audio File (MP3) *
              </label>
              <div className="flex gap-2 items-center">
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg"
                  onChange={handleAudioUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => audioInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                  style={{ 
                    backgroundColor: `${currentColors.secondary}20`,
                    color: currentColors.secondary,
                    opacity: uploading ? 0.5 : 1
                  }}
                  data-testid="upload-audio-btn"
                >
                  <Upload className="w-4 h-4" />
                  Upload Audio
                </button>
                {formData.audio_url && (
                  <span className="flex items-center gap-2 text-sm" style={{ color: currentColors.text }}>
                    <Check className="w-4 h-4 text-green-500" />
                    Audio ready
                  </span>
                )}
              </div>
              {formData.audio_url && (
                <div className="mt-2">
                  <audio controls className="w-full h-10" style={{ filter: 'sepia(20%) saturate(70%)' }}>
                    <source src={formData.audio_url.startsWith('http') ? formData.audio_url : `${API.replace('/api', '')}${formData.audio_url}`} type="audio/mpeg" />
                  </audio>
                </div>
              )}
              <p className="text-xs mt-2" style={{ color: currentColors.textMuted }}>
                Or enter URL manually:
              </p>
              <input
                type="url"
                value={formData.audio_url}
                onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                className="cosmic-input w-full mt-1"
                placeholder="https://example.com/ocean-waves.mp3"
                required
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                className={`w-12 h-6 rounded-full transition-all relative ${formData.is_active ? '' : 'opacity-50'}`}
                style={{ backgroundColor: formData.is_active ? currentColors.primary : currentColors.textMuted }}
              >
                <div 
                  className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-all ${
                    formData.is_active ? 'right-0.5' : 'left-0.5'
                  }`}
                />
              </button>
              <span style={{ color: currentColors.text }}>
                {formData.is_active ? 'Active' : 'Inactive'} 
                <span className="text-xs ml-2" style={{ color: currentColors.textMuted }}>
                  (Inactive sounds won't appear in timer)
                </span>
              </span>
            </div>

            {/* Upload Progress */}
            {uploadProgress && (
              <div 
                className="text-sm text-center py-2 rounded"
                style={{ 
                  backgroundColor: uploadProgress.includes('success') ? 'rgba(34, 197, 94, 0.2)' : `${currentColors.primary}20`,
                  color: uploadProgress.includes('success') ? '#22c55e' : currentColors.primary
                }}
              >
                {uploadProgress}
              </div>
            )}

            <button 
              type="submit" 
              className="cosmic-btn w-full flex items-center justify-center gap-2"
              disabled={!formData.audio_url}
              data-testid="save-sound-btn"
            >
              <Save className="w-5 h-5" />
              <span>{editingKey ? 'Update Sound' : 'Create Sound'}</span>
            </button>
          </form>
        </div>
      )}

      {/* Sounds List */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Sound</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Key</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Status</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Preview</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sounds.map((sound) => {
                const IconComponent = getIconComponent(sound.icon);
                return (
                  <tr 
                    key={sound.sound_key} 
                    style={{ borderBottom: `1px solid ${currentColors.border}` }}
                    className="hover:opacity-80"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${currentColors.primary}20` }}
                        >
                          <IconComponent className="w-5 h-5" style={{ color: currentColors.primary }} />
                        </div>
                        <div>
                          <p style={{ color: currentColors.text }}>{sound.name}</p>
                          {sound.description && (
                            <p className="text-xs line-clamp-1" style={{ color: currentColors.textMuted }}>{sound.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <code 
                        className="text-xs px-2 py-1 rounded"
                        style={{ backgroundColor: `${currentColors.textMuted}10`, color: currentColors.textMuted }}
                      >
                        {sound.sound_key}
                      </code>
                    </td>
                    <td className="py-3 px-4">
                      <span 
                        className={`px-3 py-1 text-xs rounded-full`}
                        style={{ 
                          backgroundColor: sound.is_active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                          color: sound.is_active ? '#22c55e' : '#ef4444'
                        }}
                      >
                        {sound.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => togglePlayPreview(sound)}
                        className="p-2 rounded-lg transition-all"
                        style={{ 
                          backgroundColor: playingSound === sound.sound_key ? `${currentColors.primary}30` : `${currentColors.primary}10`,
                          color: currentColors.primary
                        }}
                        data-testid={`play-${sound.sound_key}`}
                      >
                        {playingSound === sound.sound_key ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(sound)}
                          className="p-2 rounded-lg transition-all"
                          style={{ color: currentColors.accent }}
                          title="Edit"
                          data-testid={`edit-${sound.sound_key}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(sound.sound_key)}
                          className="p-2 rounded-lg transition-all text-red-400 hover:bg-red-500/10"
                          title="Delete"
                          data-testid={`delete-${sound.sound_key}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {sounds.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-8 text-center" style={{ color: currentColors.textMuted }}>
                    No ambient sounds configured. Add sounds for ocean, rain, and forest to enhance the meditation timer!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
